// src/components/shared/PDFViewer.tsx
import React, { useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import styles from '../../assets/styles/PDFViewer.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

interface PDFViewerProps {
  pdfUrl: string;
  onClose?: () => void;
  language?: 'EN' | 'ES';
  translations?: {
    pdfViewer: {
      loading: string;
      error: string;
      close: string;
    };
  };
  onScrollToBottom?: () => void;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ pdfUrl, onClose, translations, onScrollToBottom }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  // Handle scroll event to detect when user scrolls to bottom
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollToBottom) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    
    // If user has scrolled to about 90% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onScrollToBottom();
    }
  };

  // If this is used in overlay mode with close button
  if (onClose) {
    return (
      <div className={styles.overlay} onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}>
        <div 
          className={styles.pdfContainer}
          ref={containerRef}
          onScroll={handleScroll}
        >
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={translations?.pdfViewer.loading || "Loading PDF..."}
            error={translations?.pdfViewer.error || "Failed to load PDF document."}
          >
            <Page 
              pageNumber={pageNumber} 
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
          {numPages && (
            <div className={styles.controls}>
              <button 
                onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
                disabled={pageNumber <= 1}
              >
                ←
              </button>
              <span>
                {pageNumber} / {numPages}
              </span>
              <button 
                onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
                disabled={pageNumber >= numPages}
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // If this is used embedded without overlay
  return (
    <div 
      className={styles.embeddedContainer || styles.pdfContainer}
      ref={containerRef}
      onScroll={handleScroll}
      style={{ height: '100%', overflow: 'auto' }}
    >
      <Document
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading="Loading PDF..."
        error="Failed to load PDF document."
      >
        <Page 
          pageNumber={pageNumber} 
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
      {numPages && (
        <div className={styles.controls}>
          <button 
            onClick={() => setPageNumber(prev => Math.max(1, prev - 1))}
            disabled={pageNumber <= 1}
          >
            ←
          </button>
          <span>
            {pageNumber} / {numPages}
          </span>
          <button 
            onClick={() => setPageNumber(prev => Math.min(numPages, prev + 1))}
            disabled={pageNumber >= numPages}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

export default PDFViewer;